import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdAdd,
  MdChevronLeft,
  MdChevronRight,
  MdEventBusy,
  MdFilterListOff,
  MdFirstPage,
  MdLastPage,
  MdRefresh,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { todayIso } from '@utils/format';
import { MarketClosuresGrid } from '@features/marketClosures/components/MarketClosuresGrid';
import { MarketClosureFormDialog } from '@features/marketClosures/components/MarketClosureFormDialog';
import {
  useClosures,
  useDeleteClosure,
} from '@features/marketClosures/hooks';
import type {
  Closure,
  ClosureSortKey,
  ListClosuresParams,
  SortDirection,
} from '@features/marketClosures/schema';

const DEFAULT_PAGE_SIZE = 50;

export default function MarketClosuresScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isActive, setIsActive] = useState<boolean | undefined>(true); // default: hide disabled
  const [includeUpcomingOnly, setIncludeUpcomingOnly] = useState(false); // default: show all (past + future); opt-in to upcoming-only
  const [sort, setSort] = useState<{
    sortBy?: ClosureSortKey;
    sortDir?: SortDirection;
  }>({});

  // Debounce search input so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever filters or sort change.
  useEffect(() => {
    setPage(1);
  }, [searchText, isActive, includeUpcomingOnly, pageSize, sort]);

  const params = useMemo<ListClosuresParams>(
    () => ({
      page,
      pageSize,
      searchText: searchText || undefined,
      isActive,
      dateFrom: includeUpcomingOnly ? todayIso() : undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, searchText, isActive, includeUpcomingOnly, sort],
  );

  const { data, isLoading, isFetching, refetch } = useClosures(params);
  const deleteMutation = useDeleteClosure();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Closure | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: Closure) => {
    setEditing(row);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleDelete = (row: Closure) => {
    if (
      window.confirm(
        `Delete "${row.name}"? This permanently removes the closure and all APMC links. Use the Active toggle instead if you want to disable it.`,
      )
    ) {
      deleteMutation.mutate(row.id);
    }
  };

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  // Active = deviates from defaults (Active status, show-all). Drives whether
  // an empty result reads "No matches (clear filters)" vs "No closures yet".
  const hasActiveFilters =
    Boolean(searchText) || isActive !== true || includeUpcomingOnly;
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setIsActive(true);
    setIncludeUpcomingOnly(false);
  };

  return (
    <Page
      title="Market closures"
      subtitle="Holidays, strikes, and renovations — which markets are closed when."
      fullHeight
      topBarAction={
        <Button
          variant="contained"
          size="small"
          startIcon={<MdAdd />}
          onClick={openCreate}
        >
          New
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-xl bg-surface-muted px-4 py-3">
        <TextField
          label="Search by name"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="min-w-[220px] flex-1"
        />
        <TextField
          label="Status"
          size="small"
          select
          value={isActive === undefined ? '' : isActive ? 'active' : 'disabled'}
          onChange={(e) => {
            const v = e.target.value;
            setIsActive(v === '' ? undefined : v === 'active');
          }}
          sx={{ width: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="disabled">Disabled</MenuItem>
        </TextField>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeUpcomingOnly}
              onChange={(e) => setIncludeUpcomingOnly(e.target.checked)}
            />
          }
          label={<span className="text-[13px]">Upcoming + ongoing only</span>}
        />
        <TextField
          label="Page size"
          size="small"
          select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          sx={{ width: 110 }}
        >
          {[25, 50, 100].map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>

        <div className="ml-auto flex items-center gap-2">
          <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
            <span>
              <IconButton
                onClick={() => refetch()}
                size="small"
                disabled={isFetching}
              >
                {isFetching ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <MdRefresh />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '2fr', bar: 'w-3/4' },
              { width: '220px', bar: 'w-2/3' },
              { width: '140px', bar: 'w-16' },
              { width: '130px', bar: 'w-20' },
              { width: '180px', bar: 'w-24' },
              { width: '130px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="Nothing matches the current filters. Try widening your search."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdEventBusy}
              title="No closures yet"
              message="Create your first closure (holiday, strike, renovation) to schedule downtime for one or more APMCs."
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MdAdd />}
                  onClick={openCreate}
                >
                  New closure
                </Button>
              }
            />
          )
        ) : (
          <MarketClosuresGrid
            data={data}
            onEdit={openEdit}
            onDelete={handleDelete}
            onSortChange={setSort}
          />
        )}
      </div>

      <div className="mt-4 flex items-center justify-between px-1">
        <span className="text-[12.5px] text-foreground-muted">
          {totalRecords > 0 ? (
            <>
              Showing{' '}
              <span className="font-semibold text-foreground">
                {(page - 1) * pageSize + 1}–
                {Math.min(page * pageSize, totalRecords)}
              </span>{' '}
              of{' '}
              <span className="font-semibold text-foreground">
                {totalRecords}
              </span>
            </>
          ) : (
            'No records'
          )}
        </span>

        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-1.5 py-1 shadow-soft">
          <PgButton
            disabled={page <= 1}
            onClick={() => setPage(1)}
            ariaLabel="First page"
          >
            <MdFirstPage size={16} />
          </PgButton>
          <PgButton
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            ariaLabel="Previous page"
          >
            <MdChevronLeft size={16} />
          </PgButton>
          <span className="px-2 text-[12.5px] tabular-nums text-foreground-muted">
            <span className="font-semibold text-foreground">{page}</span>
            <span className="mx-1 text-border-strong">/</span>
            <span>{totalPages}</span>
          </span>
          <PgButton
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            ariaLabel="Next page"
          >
            <MdChevronRight size={16} />
          </PgButton>
          <PgButton
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            ariaLabel="Last page"
          >
            <MdLastPage size={16} />
          </PgButton>
        </div>
      </div>

      <MarketClosureFormDialog
        open={dialogOpen}
        closure={editing}
        onClose={closeDialog}
      />
    </Page>
  );
}

function PgButton({
  disabled,
  onClick,
  ariaLabel,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      className="flex h-6 w-6 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/[0.1] disabled:cursor-not-allowed disabled:text-border-strong disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
